// HU-29 (MG-ILE-57) · tarea 29-5. Administración › Maestro de productos por país.
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-57.md`. Lo que esta pantalla hace:
//
//   · El administrador elige el `.xlsx` de la plantilla; se lee EN EL NAVEGADOR (el archivo no
//     viaja) y «Cargar el maestro» manda UNA petición compacta (AT S3 §2.2.6, D5).
//   · El resultado es la respuesta del servidor tal cual: `texto_de_resultado` y las filas
//     rechazadas con su número, SKU y motivo (C1, C3, C4). Si falta una columna entera, el 422
//     dice cuál y que no se cargó nada (C2).
//   · El historial (C7) se vuelve a pedir tras cada carga (§8.1 R3); «Ver detalle» trae la carga
//     guardada con sus rechazos.
//   · Solo cambian los países del archivo (C5, C6): eso se comprueba en el selector del registro.
//
//   · CC-02 bloque B4 (MG-ILE-1074): la plantilla trae la columna opcional «Autoridad» (vacía: la
//     predeterminada del país) y la tarjeta «Registros por autoridad» (`components/ProductosDelPais`)
//     abre la ficha de un producto para marcar o desmarcar autoridades sin volver a cargar el archivo.
//
// «Descargar plantilla»: el AT no tiene ruta de plantilla (vacío 7 de la spec). Se sirve el
// `.xlsx` de `04_analisis_funcional` como archivo estático del frontend (`public/plantillas/`):
// propuesta anotada en el checklist; si se decide un endpoint, solo cambia la URL.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Download, Eye, FileSpreadsheet, History, PackageSearch, RotateCcw, ShieldOff, Upload } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Mono, Modal, PageHeader, SubidaDeArchivo,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import { cargarMaestro, detalleDeCarga, historialDeCargas } from '../api/maestro';
import type { CargaDeMaestro, CargaDelHistorial, DetalleDeCarga, RechazoDeFila, ResultadoDeCarga } from '../api/maestro';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { esArchivoDelMaestro, leerMaestro } from '../lib/leerMaestro';
import { comoFechaYHora } from '../lib/fechas';
import { descargarBlob } from '../lib/entradas';
import { useCatalogosOpcional } from '../lib/CatalogosContexto';
import ProductosDelPais from '../components/ProductosDelPais';

const SIN_ACCESO = 'No tienes acceso a esta opción';
const URL_DE_LA_PLANTILLA = '/plantillas/plantilla_maestro_productos.xlsx';
/** Textos fijados por la spec (Campos · Carga). */
const MENSAJE_FORMATO = 'El maestro se carga desde un archivo Excel (.xlsx).';
const MENSAJE_SIN_ARCHIVO = 'Elige el archivo.';
const ACEPTA_XLSX = { accept: '.xlsx', texto: 'Excel (.xlsx) con la plantilla del maestro' };
const FILAS_DE_LA_VISTA_PREVIA = 10;

const COLUMNAS_DE_RECHAZOS: ColumnaDeTabla[] = [
  { key: 'numero_de_fila', label: 'Fila', numeric: true, className: 'w-20' },
  { key: 'sku', label: 'SKU', className: 'w-40' },
  { key: 'motivo', label: 'Motivo' },
];

const COLUMNAS_DEL_HISTORIAL: ColumnaDeTabla[] = [
  { key: 'cargado_en', label: 'Fecha y hora' },
  { key: 'cargado_por', label: 'Autor' },
  { key: 'nombre_archivo', label: 'Archivo' },
  { key: 'filas_aceptadas', label: 'Aceptadas', numeric: true },
  { key: 'filas_rechazadas', label: 'Rechazadas', numeric: true },
  { key: 'acciones', label: '' },
];

const celdaDeRechazo = (fila: FilaDeTabla, columna: ColumnaDeTabla): ReactNode => {
  const r = fila as unknown as RechazoDeFila;
  if (columna.key === 'sku') return r.sku ? <Mono>{r.sku}</Mono> : null;
  if (columna.key === 'numero_de_fila') return r.numero_de_fila;
  return r.motivo;
};

function TablaDeRechazos({ rechazos, testid }: { rechazos: RechazoDeFila[]; testid: string }) {
  return (
    <DataTable sinMarco columns={COLUMNAS_DE_RECHAZOS} data-testid={testid} testidDeFila={`${testid}-fila`} regionDesplazable="Filas rechazadas"
      rows={rechazos.map((r) => ({ ...r, id: `${r.numero_de_fila}-${r.sku ?? ''}` }))} renderCell={celdaDeRechazo} />
  );
}

export default function MaestroDeProductos() {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [lectura, setLectura] = useState<CargaDeMaestro | null>(null);
  const [errorDelArchivo, setErrorDelArchivo] = useState<string | null>(null);
  const [fase, setFase] = useState<'quieto' | 'leyendo' | 'enviando'>('quieto');
  const [resultado, setResultado] = useState<ResultadoDeCarga | null>(null);
  const [falloDeEnvio, setFalloDeEnvio] = useState<Fallo | null>(null);
  const [historial, setHistorial] = useState<CargaDelHistorial[] | null>(null);
  const [falloDeCarga, setFalloDeCarga] = useState<Fallo | null>(null);
  const [detalle, setDetalle] = useState<DetalleDeCarga | null>(null);
  const [falloDelDetalle, setFalloDelDetalle] = useState<Fallo | null>(null);
  // CC-02 (B4): tras cada carga, la tarjeta de registros por autoridad vuelve a pedir su lista.
  const [cargasHechas, setCargasHechas] = useState(0);
  const catalogos = useCatalogosOpcional();

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargarHistorial = useCallback(async () => {
    setFalloDeCarga(null);
    try {
      const lista = await historialDeCargas();
      if (vivo.current) setHistorial(lista);
    } catch (e) {
      if (vivo.current) setFalloDeCarga(aFallo(e));
    }
  }, []);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  /** Elegir el archivo: se comprueba el formato y se lee para la vista previa. */
  const elegir = async (elegido: File | null) => {
    setArchivo(elegido);
    setLectura(null);
    setResultado(null);
    setFalloDeEnvio(null);
    setErrorDelArchivo(null);
    if (!elegido) return;
    if (!esArchivoDelMaestro(elegido)) {
      setErrorDelArchivo(MENSAJE_FORMATO);
      return;
    }
    setFase('leyendo');
    try {
      const leida = await leerMaestro(elegido);
      if (vivo.current) setLectura(leida);
    } catch {
      if (vivo.current) setErrorDelArchivo(MENSAJE_FORMATO);
    } finally {
      if (vivo.current) setFase('quieto');
    }
  };

  const cargar = async () => {
    setResultado(null);
    setFalloDeEnvio(null);
    if (!archivo) { setErrorDelArchivo(MENSAJE_SIN_ARCHIVO); return; }
    if (!lectura) return;
    setFase('enviando');
    try {
      const respuesta = await cargarMaestro(lectura);
      if (!vivo.current) return;
      setResultado(respuesta);
      setArchivo(null);
      setLectura(null);
      setCargasHechas((n) => n + 1);
      cargarHistorial();
    } catch (e) {
      if (vivo.current) setFalloDeEnvio(aFallo(e));
    } finally {
      if (vivo.current) setFase('quieto');
    }
  };

  const descargarPlantilla = async () => {
    try {
      const respuesta = await fetch(URL_DE_LA_PLANTILLA);
      if (!respuesta.ok) throw new Error(String(respuesta.status));
      descargarBlob(await respuesta.blob(), 'plantilla_maestro_productos.xlsx');
    } catch {
      setFalloDeEnvio({ message: 'No se pudo descargar la plantilla. Intenta de nuevo.' });
    }
  };

  const verDetalle = async (cargaId: string) => {
    setFalloDelDetalle(null);
    try {
      const d = await detalleDeCarga(cargaId);
      if (vivo.current) setDetalle(d);
    } catch (e) {
      if (vivo.current) setFalloDelDetalle(aFallo(e));
    }
  };

  const encabezado = (
    <PageHeader
      eyebrow="Administración · Maestro de productos"
      hu="HU-29 · D-S3-15"
      title="Maestro de productos"
      subtitle="Los productos que el registro ofrece en cada país, con su registro sanitario. Se cargan desde la plantilla de Excel; solo cambian los países que trae el archivo."
      icon={PackageSearch}
      actions={!(falloDeCarga?.estado === 403) && (
        <Button variant="secondary" icon={Download} onClick={() => { descargarPlantilla(); }} data-testid="maestro-descargar-plantilla">
          Descargar plantilla
        </Button>
      )}
    />
  );

  // HU-29-C11: solo el administrador. El 403 llega del historial (y lo repetiría la carga).
  if (falloDeCarga?.estado === 403 || falloDeEnvio?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={ShieldOff} title={SIN_ACCESO} data-testid="maestro-sin-permiso"
          description="El maestro de productos lo carga el administrador del sistema." />
      </>
    );
  }

  const ocupado = fase !== 'quieto';
  const vistaPrevia = lectura?.filas.slice(0, FILAS_DE_LA_VISTA_PREVIA) ?? [];

  return (
    <>
      {encabezado}

      <Card title="Cargar el maestro" icon={Upload} className="mb-4"
        subtitle="Una fila por producto y país; si un producto está registrado ante dos autoridades del país, una fila por autoridad (columna Autoridad, opcional: vacía es la predeterminada). Un producto que falta en el archivo de su país deja de ofrecerse para casos nuevos; los casos ya registrados no cambian.">
        <div className="space-y-4">
          <Field label="Archivo" required error={errorDelArchivo ?? undefined}>
            <SubidaDeArchivo aria-label="Archivo del maestro" archivo={archivo} acepta={ACEPTA_XLSX} disabled={ocupado}
              onElegir={(f) => { elegir(f); }} data-testid="maestro-form-archivo" />
          </Field>

          {lectura && vistaPrevia.length > 0 && (
            <div>
              <p className="text-meta text-ink-soft mb-2">
                Vista previa: {vistaPrevia.length} de {lectura.filas.length} filas leídas del archivo.
              </p>
              <div className="border border-hairline rounded-xl overflow-hidden">
                <DataTable sinMarco data-testid="maestro-previa" regionDesplazable="Vista previa del archivo"
                  columns={[{ key: 'fila', label: 'Fila', numeric: true }, ...lectura.columnas.map((c, i) => ({ key: `c${i}`, label: c }))]}
                  rows={vistaPrevia.map(([numero, ...valores]) => ({
                    id: String(numero), fila: numero, ...Object.fromEntries(valores.map((v, i) => [`c${i}`, v])),
                  }))} />
              </div>
            </div>
          )}

          {ocupado && (
            <p data-testid="maestro-cargando" role="status" className="text-body text-ink-soft">
              {fase === 'leyendo' ? 'Leyendo el archivo…' : 'Cargando el maestro…'}
            </p>
          )}

          {falloDeEnvio && (
            <Alert tone="danger" title="No se cargó el maestro" data-testid="maestro-error-envio" detalleTecnico={falloDeEnvio.detalleTecnico}>
              <span data-testid={falloDeEnvio.estado === 422 ? 'maestro-resultado-columnas' : undefined}>{falloDeEnvio.message}</span>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button icon={FileSpreadsheet} onClick={() => { cargar(); }} disabled={ocupado} data-testid="maestro-cargar">
              {fase === 'enviando' ? 'Cargando…' : 'Cargar el maestro'}
            </Button>
          </div>
        </div>
      </Card>

      {resultado && (
        <div className="mb-4 space-y-3" data-testid="maestro-exito">
          <Alert tone="success" title="Maestro cargado" onCerrar={() => setResultado(null)}>
            <span data-testid="maestro-resultado">{resultado.texto_de_resultado}</span>
          </Alert>
          {resultado.rechazos.length > 0 && (
            <Card title="Filas rechazadas" subtitle="Las demás filas entraron; estas no se cargaron." pad={false}>
              <TablaDeRechazos rechazos={resultado.rechazos} testid="maestro-resultado-filas" />
            </Card>
          )}
        </div>
      )}

      <ProductosDelPais version={cargasHechas}
        paises={(catalogos?.PAISES ?? []).map((p) => ({ cod: p.cod, nombre: p.nombre }))} />

      <Card title="Historial de cargas" icon={History} pad={false}>
        {falloDeCarga && (
          <div className="p-5 space-y-3">
            <Alert tone="danger" title="No se pudo cargar el historial" data-testid="maestro-error" detalleTecnico={falloDeCarga.detalleTecnico}>
              {falloDeCarga.message}{falloDeCarga.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
            </Alert>
            <Button variant="secondary" icon={RotateCcw} onClick={() => { cargarHistorial(); }} data-testid="maestro-reintentar">Reintentar</Button>
          </div>
        )}
        {!falloDeCarga && historial === null && (
          <p data-testid="maestro-cargando" role="status" className="p-5 text-body text-ink-soft">Cargando el historial…</p>
        )}
        {!falloDeCarga && historial && (
          <DataTable sinMarco columns={COLUMNAS_DEL_HISTORIAL} data-testid="maestro-historial" testidDeFila="maestro-historial-fila" regionDesplazable="Historial de cargas"
            rows={historial.map((c) => ({ ...c, id: c.carga_id }))}
            empty={(
              <EmptyState icon={FileSpreadsheet} data-testid="maestro-vacio"
                title="Todavía no se cargó ningún maestro. Descarga la plantilla y súbela con los productos." />
            )}
            renderCell={(fila, columna) => {
              const c = fila as unknown as CargaDelHistorial;
              // RN-82: una carga del maestro no es de ningún caso ni de un solo país: su hora es la de Lima.
              if (columna.key === 'cargado_en') return <Mono>{comoFechaYHora(c.cargado_en, null)}</Mono>;
              if (columna.key === 'nombre_archivo') return c.nombre_archivo ? <Mono className="break-all">{c.nombre_archivo}</Mono> : null;
              if (columna.key === 'acciones') {
                return (
                  <Button size="sm" variant="ghost" icon={Eye} data-testid="maestro-historial-fila-ver"
                    aria-label={`Ver detalle de la carga ${c.nombre_archivo ?? ''}`} onClick={() => { verDetalle(c.carga_id); }}>
                    Ver detalle
                  </Button>
                );
              }
              return fila[columna.key] as ReactNode;
            }} />
        )}
        {falloDelDetalle && (
          <div className="p-5">
            <Alert tone="danger" title="No se pudo abrir el detalle" detalleTecnico={falloDelDetalle.detalleTecnico}>{falloDelDetalle.message}</Alert>
          </div>
        )}
      </Card>

      <Modal open={detalle !== null} onClose={() => setDetalle(null)} ancho="xl" data-testid="maestro-detalle"
        eyebrow="Administración · Maestro de productos" title={`Carga ${detalle?.nombre_archivo ?? ''}`} icon={History}
        footer={<Button variant="secondary" onClick={() => setDetalle(null)}>Cerrar</Button>}>
        {detalle && (
          <div className="space-y-3">
            <p className="text-body">
              <Mono>{comoFechaYHora(detalle.cargado_en, null)}</Mono> · {detalle.cargado_por} · {detalle.filas_aceptadas} aceptadas · {detalle.filas_rechazadas} rechazadas
            </p>
            {detalle.detalle_rechazos.length > 0
              ? <div className="border border-hairline rounded-xl overflow-hidden"><TablaDeRechazos rechazos={detalle.detalle_rechazos} testid="maestro-detalle-filas" /></div>
              : <p className="text-body text-ink-soft">Ninguna fila rechazada.</p>}
          </div>
        )}
      </Modal>
    </>
  );
}
